import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryService from '../../../services/category.service';
import EventService from '../../../services/event.service';
import SalePointService from '../../../services/salePoint.service';

const styles = {
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '12px',
      minWidth: '500px'
    }
  },
  dialogTitle: {
    display: 'flex', 
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e0e0e0'
  },
  form: {
    display: 'flex',
    flexDirection: 'column', 
    gap: '20px',
    padding: '24px'
  },
  actions: {
    padding: '16px 24px',
    borderTop: '1px solid #e0e0e0'
  },
  submitButton: {
    backgroundColor: 'primary.main',
    borderRadius: '8px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#333'
    }
  },
  cancelButton: {
    color: '#666',
    borderRadius: '8px',
    textTransform: 'none'
  },
  imagePreview: {
    width: '100%',
    maxWidth: '250px',
    height: '150px',
    objectFit: 'cover',
    borderRadius: '8px',
    border: '1px solid #e0e0e0'
  }
};

function Form({ open, onClose, initialData = null, onSubmit }) {
  const [formData, setFormData] = React.useState({
    name: '',
    description: '',
    price: '',
    event_id: '',
    category_id: '',
    sale_points: {},
    image: ''
  });

  const [categories, setCategories] = React.useState([]);
  const [events, setEvents] = React.useState([]);
  const [salePoints, setSalePoints] = React.useState([]);

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesData, eventsData, salePointsData] = await Promise.all([
        CategoryService.list(),
        EventService.list(),
        SalePointService.list()
      ]);
      setCategories(categoriesData);
      setEvents(eventsData);
      setSalePoints(salePointsData);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    }
  };

  React.useEffect(() => {
    if (initialData) {
      const salePointsQuantities = {};
      initialData.product_sale_points?.forEach(sp => {
        salePointsQuantities[sp.salePoint.id] = sp.quantity;
      });

      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        price: initialData.price || '',
        event_id: initialData.event?.id || '',
        category_id: initialData.category?.id || '',
        sale_points: salePointsQuantities,
        image: ''
      });
    } else {
      const emptySalePoints = {};
      salePoints.forEach(sp => {
        emptySalePoints[sp.id] = '';
      });

      setFormData({
        name: '',
        description: '',
        price: '',
        event_id: '',
        category_id: '',
        sale_points: emptySalePoints,
        image: ''
      });
    }
  }, [initialData, salePoints]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSalePointChange = (salePointId, quantity) => {
    setFormData(prev => ({
      ...prev,
      sale_points: {
        ...prev.sale_points,
        [salePointId]: quantity
      }
    }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          image: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      const salePointsArray = Object.entries(formData.sale_points)
        .filter(([_, quantity]) => quantity !== '')
        .map(([id, quantity]) => ({
          id: parseInt(id),
          quantity: parseInt(quantity)
        }));

      const cleanedFormData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        event_id: parseInt(formData.event_id),
        category_id: parseInt(formData.category_id),
        sale_points: salePointsArray,
        image: formData.image
      };
      onSubmit(cleanedFormData);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      sx={styles.dialog}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <span>{initialData ? 'Modifier le produit' : 'Nouveau produit'}</span>
        <IconButton 
          onClick={onClose}
          size="small"
          sx={{ color: '#666' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={styles.form}>
          <Box sx={{ mb: 2 }}>
            {(formData.image || initialData?.image) && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Image actuelle
                </Typography>
                <img 
                  src={formData.image || `${process.env.REACT_APP_BASE_URL}/${initialData.image}`}
                  alt="Aperçu du produit"
                  style={styles.imagePreview}
                />
              </Box>
            )}
            <TextField
              fullWidth
              type="file"
              label="Image du produit"
              onChange={handleImageChange}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
            />
          </Box>

          <TextField
            fullWidth
            label="Nom"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />
          
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            multiline
            rows={4}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

          <TextField
            fullWidth
            label="Prix"
            name="price"
            type="number"
            value={formData.price}
            onChange={handleChange}
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

          <TextField
            fullWidth
            select
            label="Catégorie"
            name="category_id"
            value={formData.category_id}
            onChange={handleChange}
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          >
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            select
            label="Événement"
            name="event_id"
            value={formData.event_id}
            onChange={handleChange}
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          >
            {events.map((event) => (
              <MenuItem key={event.id} value={event.id}>
                {event.name}
              </MenuItem>
            ))}
          </TextField>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Points de vente
            </Typography>
            <Grid container spacing={2}>
              {salePoints.map((salePoint) => (
                <Grid item xs={12} key={salePoint.id} sx={{mb: 1}}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={6}>
                      <Typography>{salePoint.name}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="number"
                        size="small"
                        placeholder="Quantité"
                        value={formData.sale_points[salePoint.id]}
                        onChange={(e) => handleSalePointChange(salePoint.id, e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px'
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={styles.actions}>
          <Button 
            onClick={onClose}
            sx={styles.cancelButton}
          >
            Annuler
          </Button>
          <Button 
            type="submit"
            variant="contained"
            sx={styles.submitButton}
          >
            {initialData ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

Form.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    price: PropTypes.number,
    event: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    }),
    category: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    }),
    product_sale_points: PropTypes.arrayOf(PropTypes.shape({
      salePoint: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string
      }),
      quantity: PropTypes.number
    })),
    image: PropTypes.string
  })
};

export default Form;